import { useCallback, useEffect, useRef, useState } from 'react';
import { NEUROTOX_PULSE_DURATION_MS } from '../config/neurotoxPulseEffect';
import { playSoundEffect } from '../hooks/useSoundEffects';

function mapNeurotoxToViewerSlot(fx, viewerUserId) {
  if (!fx || fx.zone == null || fx.slotIndex == null || fx.targetPlayerId == null) {
    return null;
  }
  const isOwnerViewer = String(fx.targetPlayerId) === String(viewerUserId);
  const prefix = isOwnerViewer ? 'your' : 'opponent';
  return {
    zone: `${prefix}_${fx.zone}`,
    index: fx.slotIndex,
  };
}

const SHRINK_CHEVRON_STRATEGEM_IDS = new Set([53, 180, 183]);

function isNeurotoxPulseStrategem(s) {
  if (!s || s.instanceId == null) return false;
  if (!SHRINK_CHEVRON_STRATEGEM_IDS.has(Number(s.id))) return false;
  const t = (s.type && String(s.type)) || '';
  if (!t) return true;
  return /strategem/i.test(t);
}

function hasNeurotoxBattlefieldShrinkPulseFx(unit) {
  const fx = unit?.neurotoxPulseFx;
  return (
    fx != null
    && fx.zone != null
    && fx.slotIndex != null
    && fx.targetPlayerId != null
    && typeof fx.pulseSeq === 'number'
    && fx.pulseSeq > 0
  );
}

function neurotoxBattlefieldShrinkDedupeKey(ownerId, unit) {
  const pulseSeq = unit.neurotoxPulseFx?.pulseSeq ?? 0;
  return `ntoxshrink-${ownerId}-${unit.instanceId}-${pulseSeq}`;
}

function seedNeurotoxPulseProcessed(gameState, processedRef) {
  Object.keys(gameState.state).forEach((pid) => {
    gameState.state[pid]?.completedOrders?.forEach((st) => {
      if (isNeurotoxPulseStrategem(st)) {
        processedRef.current.add(`ntox${st.id}-${pid}-${st.instanceId}`);
      }
    });
    ['front', 'flank'].forEach((zone) => {
      (gameState.state[pid]?.playedOrders?.[zone] || []).forEach((unit) => {
        if (hasNeurotoxBattlefieldShrinkPulseFx(unit)) {
          processedRef.current.add(neurotoxBattlefieldShrinkDedupeKey(pid, unit));
        }
      });
    });
  });

  (gameState.pendingNeurotoxShrinkPulseAnimations || []).forEach((entry) => {
    if (entry?.animationKey) {
      processedRef.current.add(`ntoxpending-${entry.animationKey}`);
    }
  });
}

/**
 * One-shot WS / gameState pending queue — same dedupe keys as useNeurotoxPulse effect.
 */
export function consumePendingNeurotoxShrinkPulsesFromFragment(
  gameStateFragment,
  viewerUserId,
  setActiveNeurotoxPulses,
  processedRef
) {
  if (!gameStateFragment?.pendingNeurotoxShrinkPulseAnimations?.length) {
    return;
  }
  if (viewerUserId == null) {
    throw new Error('consumePendingNeurotoxShrinkPulsesFromFragment: viewerUserId is required');
  }
  if (!processedRef || typeof processedRef.current === 'undefined') {
    throw new Error('consumePendingNeurotoxShrinkPulsesFromFragment: processedRef is required');
  }
  if (typeof setActiveNeurotoxPulses !== 'function') {
    throw new Error('consumePendingNeurotoxShrinkPulsesFromFragment: setActiveNeurotoxPulses is required');
  }

  const pending = [...gameStateFragment.pendingNeurotoxShrinkPulseAnimations];
  delete gameStateFragment.pendingNeurotoxShrinkPulseAnimations;

  pending.forEach((entry) => {
    if (!entry?.animationKey) {
      throw new Error('consumePendingNeurotoxShrinkPulsesFromFragment: pending entry requires animationKey');
    }
    const dedupeKey = `ntoxpending-${entry.animationKey}`;
    if (processedRef.current.has(dedupeKey)) {
      return;
    }
    processedRef.current.add(dedupeKey);
    activateNeurotoxPulseSlot(entry, viewerUserId, setActiveNeurotoxPulses, {
      animationKey: entry.animationKey,
    });
  });
}

/**
 * Play Neurotox shrink chevrons on a battlefield slot (shared by strategems and deploy shrinks).
 */
export function activateNeurotoxPulseSlot(fx, viewerUserId, setActiveNeurotoxPulses, options = {}) {
  if (!fx || fx.zone == null || fx.slotIndex == null || fx.targetPlayerId == null) {
    return;
  }
  if (viewerUserId == null) {
    throw new Error('activateNeurotoxPulseSlot: viewerUserId is required');
  }

  const viewerSlot = mapNeurotoxToViewerSlot(fx, viewerUserId);
  if (!viewerSlot) {
    return;
  }

  const animKey = options.animationKey || `ntox-${Date.now()}`;
  setActiveNeurotoxPulses((prev) => [
    ...prev,
    { key: animKey, viewerSlot, animationKey: animKey },
  ]);

  if (options.playSound !== false) {
    playSoundEffect('/sounds/neurotox.mp3', 0.6);
  }

  window.setTimeout(() => {
    setActiveNeurotoxPulses((prev) => prev.filter((p) => p.key !== animKey));
  }, NEUROTOX_PULSE_DURATION_MS + 120);
}

/**
 * Strategems 53 (Neurotox Pulse), 180 (Touch of darkness), 183 (Growing Darkness):
 * same `neurotoxPulseFx` slot + NeurotoxPulseEffect chevron shrink (sounds differ per useStrategemSoundEffects).
 * Deploy unit 166 (Kendrex Toxengineer) uses the same chevrons via battlefield neurotoxPulseFx / pending queue.
 */
export function useNeurotoxPulse(gameState, viewerUserId) {
  const [activeNeurotoxPulses, setActiveNeurotoxPulses] = useState([]);
  const processedRef = useRef(new Set());
  const seededRef = useRef(false);
  const gameIdRef = useRef(null);

  const triggerPulseFromFx = useCallback((fx, options = {}) => {
    activateNeurotoxPulseSlot(fx, viewerUserId, setActiveNeurotoxPulses, options);
  }, [viewerUserId]);

  useEffect(() => {
    if (!gameState?.state || viewerUserId == null) return;

    const gid = gameState.gameId ?? null;
    if (gid != null && gameIdRef.current !== gid) {
      gameIdRef.current = gid;
      seededRef.current = false;
      processedRef.current = new Set();
      setActiveNeurotoxPulses([]);
    }

    if (!seededRef.current) {
      seedNeurotoxPulseProcessed(gameState, processedRef);
      seededRef.current = true;
      return;
    }

    consumePendingNeurotoxShrinkPulsesFromFragment(
      gameState,
      viewerUserId,
      setActiveNeurotoxPulses,
      processedRef
    );

    Object.keys(gameState.state).forEach((casterId) => {
      const player = gameState.state[casterId];
      if (!player?.completedOrders) return;

      player.completedOrders.forEach((strategem) => {
        if (!isNeurotoxPulseStrategem(strategem)) return;
        const fx = strategem.neurotoxPulseFx;
        if (!fx) return;

        const dedupeKey = `ntox${strategem.id}-${casterId}-${strategem.instanceId}`;
        if (processedRef.current.has(dedupeKey)) return;
        processedRef.current.add(dedupeKey);

        activateNeurotoxPulseSlot(fx, viewerUserId, setActiveNeurotoxPulses, {
          animationKey: `ntox${strategem.id}-${strategem.instanceId}-${Date.now()}`,
          playSound: false,
        });
      });
    });

    Object.keys(gameState.state).forEach((ownerId) => {
      const player = gameState.state[ownerId];
      if (!player?.playedOrders) return;

      ['front', 'flank'].forEach((zone) => {
        (player.playedOrders[zone] || []).forEach((shrinkUnit) => {
          if (!hasNeurotoxBattlefieldShrinkPulseFx(shrinkUnit)) return;
          const fx = shrinkUnit.neurotoxPulseFx;
          if (!fx) return;

          const dedupeKey = neurotoxBattlefieldShrinkDedupeKey(ownerId, shrinkUnit);
          if (processedRef.current.has(dedupeKey)) return;
          processedRef.current.add(dedupeKey);

          activateNeurotoxPulseSlot(fx, viewerUserId, setActiveNeurotoxPulses, {
            animationKey: `ntoxshrink-${shrinkUnit.instanceId}-${fx.pulseSeq}`,
          });
        });
      });
    });

    if (processedRef.current.size > 120) {
      const keys = Array.from(processedRef.current);
      keys.slice(0, keys.length - 80).forEach((k) => processedRef.current.delete(k));
    }
  }, [gameState, gameState?.stateVersion, viewerUserId]);

  const consumePendingShrinkPulsesFromFragment = useCallback((gameStateFragment, viewerOverride = null) => {
    consumePendingNeurotoxShrinkPulsesFromFragment(
      gameStateFragment,
      viewerOverride ?? viewerUserId,
      setActiveNeurotoxPulses,
      processedRef
    );
  }, [viewerUserId]);

  return {
    activeNeurotoxPulses,
    triggerPulseFromFx,
    consumePendingShrinkPulsesFromFragment,
    processedRef,
  };
}

export default useNeurotoxPulse;
